
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

// Current application version - should be updated with each release
const CURRENT_VERSION = '1.0.0';

// Keep track of notifications to prevent duplicates
let updateNotificationShown = false;

// Define checkForUpdates outside the component so it can be exported
export const checkForUpdates = async () => {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      
      return { updated: false, message: 'No service worker registered' };
    }
    
    // Create a message channel to communicate with the service worker
    const messageChannel = new MessageChannel();
    
    // Create a promise that will resolve when we get a response
    const updateCheckPromise = new Promise((resolve, reject) => {
      // Setup the message handler
      messageChannel.port1.onmessage = (event) => {
        
        
        if (event.data.type === 'UPDATE_AVAILABLE') {
          // Check if the available version is newer than current
          const availableVersion = event.data.version || '0.0.0';
          if (isNewerVersion(availableVersion, CURRENT_VERSION) && !updateNotificationShown) {
            updateNotificationShown = true;
            showUpdateNotification();
            resolve({ updated: true, message: 'Update available', version: availableVersion });
          } else {
            
            resolve({ updated: false, message: 'Already on latest version or notification shown' });
          }
        } else if (event.data.type === 'NO_UPDATE_AVAILABLE') {
          
          resolve({ updated: false, message: 'No update available' });
        } else if (event.data.type === 'UPDATE_CHECK_FAILED') {
          reject(new Error(`Update check failed: ${event.data.error}`));
        }
      };
    });
    
    // Show initial toast
    toast("Checking for updates...", {
      description: "Looking for the latest version of Meduler.",
      className: "bg-white border border-purple-100"
    });
    
    // Send a message to the service worker to check for updates
    registration.active.postMessage(
      { type: 'CHECK_FOR_UPDATES' },
      [messageChannel.port2]
    );
    
    // Wait for the response
    return await updateCheckPromise;
    
  } catch (error) {
    
    toast("Update check failed", {
      description: `Could not check for updates: ${error.message}`,
      className: "bg-white border border-red-100"
    });
    throw error;
  }
};

// Function to apply the update
export const applyUpdate = async () => {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration && registration.waiting) {
      // Send message to skip waiting
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      
      toast("Updating...", {
        description: "Applying the latest version. The app will refresh momentarily.",
        className: "bg-white border border-purple-100"
      });
      
      // Listen for state change to activate
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        toast("Update complete", {
          description: "The app has been updated to the latest version.",
          className: "bg-white border border-purple-100"
        });
        
        // Reload the page after a short delay to show the toast
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      });
      
      return { success: true };
    } else {
      
      return { success: false, reason: 'No waiting service worker found' };
    }
  } catch (error) {
    
    toast("Update failed", {
      description: `Could not apply the update: ${error.message}`,
      className: "bg-white border border-red-100"
    });
    throw error;
  }
};

// Helper function to compare version strings (semver)
function isNewerVersion(availableVersion, currentVersion) {
  const availableParts = availableVersion.split('.').map(Number);
  const currentParts = currentVersion.split('.').map(Number);
  
  for (let i = 0; i < availableParts.length; i++) {
    if (availableParts[i] > (currentParts[i] || 0)) {
      return true;
    } else if (availableParts[i] < (currentParts[i] || 0)) {
      return false;
    }
  }
  
  return false; // Versions are equal
}

// Centralized function to show update notification
function showUpdateNotification() {
  toast("New version available", {
    description: "A new version of Meduler is available. Update for the latest features.",
    action: {
      label: "Update",
      onClick: () => applyUpdate()
    },
    duration: 0, // Toast stays until dismissed
    className: "bg-white border border-purple-100"
  });
}

export function UpdateNotification() {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    // Skip if service worker isn't supported or notification already shown
    if (!navigator.serviceWorker || updateNotificationShown) {
      return;
    }

    // Listen for messages from the service worker
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'UPDATE_AVAILABLE' && !updateNotificationShown) {
        // Check if the available version is newer than current
        const availableVersion = event.data.version || '0.0.0';
        if (isNewerVersion(availableVersion, CURRENT_VERSION)) {
          updateNotificationShown = true;
          setUpdateAvailable(true);
          showUpdateNotification();
        } else {
          // Don't log every time, only when actually checking versions
          
        }
      }
    };

    // Add the event listener if a service worker controller exists
    let swRegistration = null;
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener('message', handleMessage);
    }
    
    // Single update check at component mount
    const checkOnce = async () => {
      if (!updateNotificationShown) {
        try {
          // Only perform the check if we haven't shown a notification yet
          swRegistration = await navigator.serviceWorker.ready;
          
          // Add event listener for any new workers
          swRegistration.addEventListener('updatefound', () => {
            const newWorker = swRegistration.installing;
            
            if (newWorker && !updateNotificationShown) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller && !updateNotificationShown) {
                  updateNotificationShown = true;
                  setUpdateAvailable(true);
                  showUpdateNotification();
                }
              });
            }
          });
        } catch (err) {
          
        }
      }
    };
    
    checkOnce();
    
    // Cleanup function
    return () => {
      if (navigator.serviceWorker && navigator.serviceWorker.controller) {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
      }
    };
  }, []);

  const applyUpdate = () => {
    // Get the waiting service worker
    navigator.serviceWorker.getRegistration().then(registration => {
      if (registration && registration.waiting) {
        // Send message to skip waiting
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        
        // Listen for state change to activate
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          window.location.reload();
        });
      }
    });
  };
  
  return null; // This component doesn't render anything visible
}
